import "./nordic.ts";
import { recordSubmission } from "./submit.ts";

/**
 * Live status dashboard. Renders 90 days of per-component uptime bars and a
 * weighted uptime %, drives the overall banner, an auto-refresh ticker, a
 * validating subscribe form, and a manual refresh button — all from a seeded
 * component model so the demo is self-contained.
 */

type DayState = "up" | "deg" | "down";
interface Component {
  name: string;
  group: string;
  bad: Record<number, DayState>;
}

const DAYS = 90;
const MS_DAY = 86_400_000;

// Seeded record: mostly operational, with a few degraded days that line up with
// the incident log. (Indexes are days-from-oldest within the 90-day window.)
const COMPONENTS: Component[] = [
  { name: "Agent API", group: "Core", bad: { 76: "deg" } },
  { name: "Agent Fleet runtime", group: "Core", bad: {} },
  { name: "Office Stack — Docs, Mail, Drive", group: "Suite", bad: { 51: "deg" } },
  { name: "Live Chat", group: "Suite", bad: {} },
  { name: "Sovereign Hosting — Helsinki", group: "Region", bad: {} },
  { name: "Sovereign Hosting — Frankfurt", group: "Region", bad: { 78: "deg" } },
  { name: "Sovereign Hosting — Stockholm", group: "Region", bad: {} },
  { name: "Dashboard & Auth", group: "Platform", bad: {} },
];

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

const today = new Date();
function dayLabel(offsetFromOldest: number): string {
  const d = new Date(today.getTime() - (DAYS - 1 - offsetFromOldest) * MS_DAY);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function render(): void {
  const host = $("components");
  host.innerHTML = "";
  let anyDeg = false;
  let anyDown = false;

  for (const c of COMPONENTS) {
    let downDays = 0;
    let degDays = 0;
    let barsHTML = "";
    for (let i = 0; i < DAYS; i++) {
      const st: DayState = c.bad[i] ?? "up";
      if (st === "down") downDays++;
      else if (st === "deg") degDays++;
      const cls = st === "down" ? " down" : st === "deg" ? " deg" : "";
      const label = st === "down" ? "Outage" : st === "deg" ? "Degraded" : "Operational";
      barsHTML += `<div class="ubar${cls}" title="${dayLabel(i)} · ${label}"></div>`;
    }
    // weighted uptime: up = 1, degraded = 0.7, down = 0
    const uptime = ((DAYS - downDays - degDays * 0.3) / DAYS) * 100;
    const todayState: DayState = c.bad[DAYS - 1] ?? "up";
    if (todayState === "deg") anyDeg = true;
    if (todayState === "down") anyDown = true;

    const stCls = todayState === "down" ? " down" : todayState === "deg" ? " deg" : "";
    const stTxt = todayState === "down" ? "Outage" : todayState === "deg" ? "Degraded" : "Operational";

    const el = document.createElement("div");
    el.className = "comp";
    el.innerHTML =
      `<div class="comp-top">` +
      `<span class="comp-name">${c.name}</span>` +
      `<span class="comp-grp">${c.group}</span>` +
      `<span class="comp-state${stCls}"><span class="pip"></span>${stTxt}</span>` +
      `</div>` +
      `<div class="uptime-bars">${barsHTML}</div>` +
      `<div class="comp-foot">` +
      `<span>${DAYS} days ago</span>` +
      `<span class="mid"><b>${uptime.toFixed(2)}%</b> uptime</span>` +
      `<span>Today</span>` +
      `</div>`;
    host.appendChild(el);
  }

  // overall banner
  const banner = $("banner");
  const title = $("bannerTitle");
  const sub = $("bannerSub");
  if (anyDown || anyDeg) {
    banner.classList.add("deg");
    const glyph = banner.querySelector(".glyph svg");
    if (glyph) {
      glyph.innerHTML =
        '<path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/>';
    }
    title.textContent = anyDown ? "Partial outage" : "Degraded performance";
    sub.textContent = "Some services are affected — see details below";
  } else {
    banner.classList.remove("deg");
    title.textContent = "All systems operational";
    sub.textContent = "Across all regions and services";
  }
}

/* ---------- updated-time ticker ---------- */
const updAgo = $("updAgo");
const updTime = $("updTime");
let lastUpdate = Date.now();
const fmtTime = (d: Date) =>
  `${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · auto-refresh 60s`;
function tick(): void {
  const s = Math.round((Date.now() - lastUpdate) / 1000);
  updAgo.textContent = s < 5 ? "just now" : s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ago`;
}
function stamp(): void {
  lastUpdate = Date.now();
  updTime.textContent = fmtTime(new Date());
  tick();
}

/* ---------- refresh button ---------- */
const rbtn = $("rbtn");
rbtn.addEventListener("click", () => {
  rbtn.classList.add("spin");
  setTimeout(() => rbtn.classList.remove("spin"), 500);
  render();
  stamp();
});

/* ---------- subscribe (real validation) ---------- */
const form = $<HTMLFormElement>("subForm");
const email = $<HTMLInputElement>("subEmail");
const msg = $("subMsg");
form.addEventListener("submit", (e) => {
  e.preventDefault();
  const v = email.value.trim();
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  msg.className = `sub-msg ${ok ? "ok" : "bad"}`;
  email.classList.toggle("err", !ok);
  if (ok) {
    recordSubmission("newsletter", {
      email: v,
      hp: form.querySelector<HTMLInputElement>('[name="hp"]')?.value ?? "",
    });
    msg.textContent = `✓ Subscribed — confirmation sent to ${v}`;
    email.value = "";
  } else {
    msg.textContent = "Please enter a valid email address.";
  }
});
email.addEventListener("input", () => email.classList.remove("err"));

/* ---------- boot ---------- */
render();
stamp();
setInterval(tick, 5000);
setInterval(() => {
  render();
  stamp();
}, 60000);
